import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import PageHeader from "../components/PageHeader";
import {useSelector} from "react-redux";
import Toast from "cogo-toast";
import {DefaultLoader} from "../../../components/DefaultLoader";
import InputField from "../../../FinalForm/InputField";
import {Form} from "react-final-form";
import ApiTariffSelectField from "../../../FinalForm/api/ApiTariffSelectField";
import DateTimeField from "../../../FinalForm/DatePickerField";
import SwitchField from "../../../FinalForm/SwitchField";
import {apiGetTicket, apiPostCreateTicket, apiPutUpdateTicket, apiDeleteTicketImage} from "./Actions";
import moment from "moment";
import {FiImage, FiSave} from "react-icons/fi";
import {FilterButton} from "../components/Button";
import {FormCard, CardHeader} from "../components/FormCard";
import SelectField from "../../../FinalForm/SelectField";
import {eventsById, campsById, resolveIds} from "../CheckTicket/ticketDataConfig";

// ─── TicketDataView ───────────────────────────────────────────────────────────
const tagBase  = {fontSize: "var(--enigoo-fs-xs)",fontWeight:600,borderRadius:"5px",padding:"3px 8px"};
const tagEvent = {...tagBase, background:"#eff6ff",color:"#2563eb",border:"1px solid #bfdbfe"};
const tagCamp  = {...tagBase, background:"#f0fdf4",color:"#16a34a",border:"1px solid #bbf7d0"};
const tagOther = {...tagBase, background:"#f8fafc",color:"#475569",border:"1px solid #e2e8f0"};

const TicketDataView = ({ticketData}) => {
    if (!ticketData) return null;
    let parsed;
    try { parsed = typeof ticketData === 'string' ? JSON.parse(ticketData) : ticketData; }
    catch { return <pre style={{fontSize: "var(--enigoo-fs-xs)",color:"#475569",background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:"6px",padding:"8px",margin:0,overflowX:"auto"}}>{ticketData}</pre>; }

    const allById    = {...eventsById, ...campsById};
    const eventNames = resolveIds(parsed.events ?? parsed.event, allById);
    const campNames  = resolveIds(parsed.camp ?? parsed.camps, allById);
    const knownKeys  = new Set(['events','event','camp','camps']);
    const unknownKeys = Object.keys(parsed).filter(k => !knownKeys.has(k));

    if (!eventNames.length && !campNames.length && !unknownKeys.length)
        return <span style={{fontSize: "var(--enigoo-fs-sm)",color:"#94a3b8"}}>Žádná data čipu</span>;

    return (
        <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
            {eventNames.length > 0 && (
                <div style={{display:"flex",flexDirection:"column",gap:"4px"}}>
                    <span style={{fontSize: "var(--enigoo-fs-2xs)",fontWeight:600,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em"}}>Nahrané události</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>{eventNames.map((n,i) => <span key={i} style={tagEvent}>{n}</span>)}</div>
                </div>
            )}
            {campNames.length > 0 && (
                <div style={{display:"flex",flexDirection:"column",gap:"4px"}}>
                    <span style={{fontSize: "var(--enigoo-fs-2xs)",fontWeight:600,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em"}}>Kemp</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}>{campNames.map((n,i) => <span key={i} style={tagCamp}>{n}</span>)}</div>
                </div>
            )}
            {unknownKeys.map(key => (
                <div key={key} style={{display:"flex",flexDirection:"column",gap:"4px"}}>
                    <span style={{fontSize: "var(--enigoo-fs-2xs)",fontWeight:600,color:"#94a3b8",textTransform:"uppercase",letterSpacing:"0.05em"}}>{key}</span>
                    <div style={{display:"flex",flexWrap:"wrap",gap:"4px"}}><span style={tagOther}>{JSON.stringify(parsed[key])}</span></div>
                </div>
            ))}
        </div>
    );
};

const TicketForm = () => {
    let [saving, setSaving] = useState(false);
    let [loading, setLoading] = useState(false);
    let [data, setData] = useState(null);
    const [showImage, setShowImage]         = useState(false);
    const [showDeleteImg, setShowDeleteImg] = useState(false);
    const [deletingImg, setDeletingImg]     = useState(false);

    let user = useSelector((state) => state.user);
    const navigate = useNavigate();
    const {id} = useParams();
    let submit;

    useEffect(() => {
        id && getData();
    }, []);

    const getData = () => {
        setLoading(true);
        apiGetTicket(user.activeAreal.id, id, (data) => {
            data.tariffId = data.tariff.id;
            setData(data);
            setLoading(false);
        }, (err) => {
            setLoading(false);
        });
    };

    const onSubmit = (values) => {
        setSaving(true);
        let ticketData = {
            "bc": values.bc,
            "dateTo": convertDate(values.dateTo),
            "dateFrom": convertDate(values.dateFrom),
            "entrance": values.entrance,
            "tariffId": values.tariffId,
            "type": values.type,
            "eventId": values.eventId,
            "blocked": values.blocked,
            "name": values.name,
            "note": values.note,
            "ticketType": values.ticketType,
            "timeFrom": values.timeFrom,
            "timeTo": values.timeTo,
        };
        if (id) {
            apiPutUpdateTicket(user.activeAreal.id, id, ticketData, (data) => {
                setSaving(false);
                getData();
                Toast.success("Úspěšně upraveno.");
            }, (err) => {
                Toast.warn("Při ukládání došlo k chybě.");
                setSaving(false);
            });
        } else {
            let dataToPost = {"eventId": values.eventId, "tickets": [ticketData]};
            apiPostCreateTicket(user.activeAreal.id, dataToPost, (data) => {
                setSaving(false);
                navigate("/ticket/list");
                Toast.success("Úspěšně uloženo.");
            }, (err) => {
                setSaving(false);
                Toast.warn("Při ukládání došlo k chybě.");
            });
        }
    };

    const handleDeleteImage = () => {
        setDeletingImg(true);
        apiDeleteTicketImage(user.activeAreal.id, data.bc,
            () => {
                setDeletingImg(false);
                setData(d => ({...d, imageBase64: null, imageUrl: null}));
                setShowDeleteImg(false);
                Toast.success('Fotka smazána.');
            },
            () => {
                setDeletingImg(false);
                Toast.error('Chyba při mazání fotky.');
            }
        );
    };

    const convertDate = (date) => {
        if (date) {
            let dateInString = date.replace('T', " ");
            dateInString += ":00";
            return dateInString;
        } else {
            return null;
        }
    };

    if (loading) return <DefaultLoader text={"Načítám vstupenku..."}/>;

    return (
        <>
            <PageHeader crumbs={[{label:"Vstupenky", to:"/ticket/list"}, {label: id ? "Úprava vstupenky" : "Nová vstupenka"}]}/>

            <Form onSubmit={onSubmit} initialValues={data ? data : {eventId: 0}}
                render={({handleSubmit, values}) => {
                    submit = handleSubmit;
                    return (
                        <>
                            {/* Hlavní údaje */}
                            <FormCard>
                                <div style={{fontSize: "var(--enigoo-fs-sm)",fontWeight:700,color:"#475569",marginBottom:"12px",paddingBottom:"8px",borderBottom:"1px solid #f1f5f9"}}>Hlavní údaje</div>
                                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:"0 16px"}}>
                                    <InputField label={"Barcode - číslo vstupenky"} name={"bc"} isMandatory={true} inputType={"text"}/>
                                    <InputField label={"Počet vstupů"} name={"entrance"} isMandatory={true} inputType={"number"} min={0}/>
                                    <DateTimeField name={"dateFrom"} isMandatory={true} valueToCompare={values.dateTo}
                                        validateFun={(date1, date2) => {
                                            if (moment(date1).isAfter(moment(date2))) return "Datum od nemůže být po datu do";
                                            else return "";
                                        }} label={"Platná od"} type={"from"} placeholder={"Datum od"} showTimeSelect={true}/>
                                    <DateTimeField name={"dateTo"} isMandatory={true} valueToCompare={values.dateFrom}
                                        validateFun={(date1, date2) => {
                                            if (moment(date2).isAfter(moment(date1))) return "Datum od nemůže být po datu do";
                                            else return "";
                                        }} label={"Platná do"} type={"to"} placeholder={"Datum do"} showTimeSelect={true}/>
                                    <div style={{gridColumn:"1 / -1"}}>
                                        <ApiTariffSelectField isMulti={false} isMandatory={true} name={"tariffId"}/>
                                    </div>
                                    <DateTimeField onlyTime={true} type={"froms"} name={"timeFrom"} label={"Platná v čase od"}
                                        valueToCompare={values.timeTo} isMandatory={false}
                                        validateFun={(date1, date2) => {
                                            if (!date1 || !date2) return "";
                                            const format = 'HH:mm';
                                            if (moment(date1, format).isAfter(moment(date2, format))) return "Časová platnost od nemůže být po platnosti do";
                                            else return "";
                                        }}/>
                                    <DateTimeField onlyTime={true} type={"to"} name={"timeTo"} label={"Platná v čase do"}
                                        isMandatory={false} valueToCompare={values.timeFrom}
                                        validateFun={(date1, date2) => {
                                            if (!date1 || !date2) return "";
                                            const format = 'HH:mm';
                                            if (moment(date1, format).isBefore(moment(date2, format))) return "Časová platnost do nemůže být před platnosti od";
                                            else return "";
                                        }}/>
                                    <SelectField name={"ticketType"} options={[
                                        {label: "Servisní karta/zaměstnanci", value: 0},
                                        {label: "Běžná vstupenka", value: 1},
                                        {label: "Permanentka/Karta s kreditem", value: 2}
                                    ]} label={"Typ vstupenky"} isMandatory={true}/>
                                    {id && <SwitchField name={"blocked"} label={"Vstupenka blokována"} isMandatory={false} trueValue={1} falseValue={0}/>}
                                </div>
                            </FormCard>

                            {/* Informace o držiteli */}
                            <FormCard>
                                <div style={{fontSize: "var(--enigoo-fs-sm)",fontWeight:700,color:"#475569",marginBottom:"12px",paddingBottom:"8px",borderBottom:"1px solid #f1f5f9"}}>Informace o držiteli</div>
                                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:"0 16px"}}>
                                    <InputField label={"Jméno"} name={"name"} isMandatory={false}/>
                                    <InputField label={"Poznámka"} name={"note"} isMandatory={false}/>
                                </div>
                            </FormCard>

                            {/* Další možnosti */}
                            <FormCard>
                                <div style={{fontSize: "var(--enigoo-fs-sm)",fontWeight:700,color:"#475569",marginBottom:"12px",paddingBottom:"8px",borderBottom:"1px solid #f1f5f9"}}>Další možnosti</div>
                                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:"0 16px"}}>
                                    <InputField label={"ID události"} name={"eventId"} isMandatory={true} inputType={"number"} min={0}/>
                                    <SelectField name={"type"} options={[
                                        {label: "Signál 1/ZTP", value: 1},
                                        {label: "Signál 2/Dítě", value: 2},
                                        {label: "Signál 3", value: 3}
                                    ]} label={"Typ vstupenky/signalizace"} isMandatory={false}/>
                                </div>
                            </FormCard>

                            <FilterButton type="button" onClick={handleSubmit} loading={saving} text="Uložit" icon={<FiSave size={12}/>} fullWidth/>
                        </>
                    );
                }}
            />

            {/* ── Data čipu ── */}
            {id && data?.ticketData && (
                <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",marginTop:"14px",overflow:"hidden"}}>
                    <CardHeader title="Data čipu"/>
                    <div style={{padding:"12px"}}>
                        <TicketDataView ticketData={data.ticketData}/>
                    </div>
                </div>
            )}

            {/* ── Fotka ── */}
            {id && data?.imageBase64 && (
                <div style={{border:"1px solid #e2e8f0",borderRadius:"10px",background:"#fff",marginTop:"14px",overflow:"hidden"}}>
                    <CardHeader
                        title={<span style={{display:"flex",alignItems:"center",gap:"6px"}}><FiImage size={13} style={{color:"#64748b"}}/>Fotka vstupenky</span>}
                        right={
                            <div style={{display:"flex",gap:"6px",alignItems:"center"}}>
                                <button type="button" onClick={() => setShowImage(v => !v)}
                                    style={{display:"flex",alignItems:"center",gap:"4px",padding:"4px 10px",background: showImage ? "#f1f5f9" : "#eff6ff",color: showImage ? "#475569" : "#2563eb",border:`1px solid ${showImage ? "#e2e8f0" : "#bfdbfe"}`,borderRadius:"6px",fontSize: "var(--enigoo-fs-xs)",fontWeight:600,cursor:"pointer"}}
                                >
                                    {showImage ? "Skrýt fotku" : "Zobrazit fotku"}
                                </button>
                                {!showDeleteImg ? (
                                    <button type="button" onClick={() => setShowDeleteImg(true)}
                                        style={{display:"flex",alignItems:"center",gap:"4px",padding:"4px 10px",background:"#fef2f2",color:"#dc2626",border:"1px solid #fecaca",borderRadius:"6px",fontSize: "var(--enigoo-fs-xs)",fontWeight:600,cursor:"pointer"}}
                                    >
                                        Smazat fotku
                                    </button>
                                ) : (
                                    <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
                                        <span style={{fontSize: "var(--enigoo-fs-sm)",color:"#475569"}}>Opravdu smazat?</span>
                                        <button type="button" onClick={handleDeleteImage} disabled={deletingImg}
                                            style={{padding:"4px 10px",background:"#dc2626",color:"#fff",border:"none",borderRadius:"6px",fontSize: "var(--enigoo-fs-xs)",fontWeight:600,cursor:"pointer"}}
                                        >
                                            {deletingImg ? '…' : 'Ano'}
                                        </button>
                                        <button type="button" onClick={() => setShowDeleteImg(false)} disabled={deletingImg}
                                            style={{padding:"4px 10px",background:"#f1f5f9",color:"#475569",border:"1px solid #e2e8f0",borderRadius:"6px",fontSize: "var(--enigoo-fs-xs)",fontWeight:600,cursor:"pointer"}}
                                        >
                                            Ne
                                        </button>
                                    </div>
                                )}
                            </div>
                        }
                    />
                    {showImage && (
                        <div style={{padding:"12px",display:"flex",justifyContent:"center"}}>
                            <img src={data.imageBase64} alt="Fotka vstupenky"
                                style={{maxWidth:"100%",maxHeight:"400px",borderRadius:"8px",border:"1px solid #e2e8f0"}}/>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};
export default TicketForm;
